import { useTranslation } from '@rhino-project/core/i18n';
import React, { useMemo, useState } from 'react';
import {
  useBaseOwnerFilters,
  useGlobalComponentForAttribute,
  useModelAndAttributeFromPath,
  useModelContext,
  useModelIndex
} from '@rhino-project/core/hooks';
import {
  FieldBoolean,
  FieldBooleanProps,
  FieldCountry,
  FieldCountryProps,
  FieldCurrency,
  FieldCurrencyProps,
  FieldDate,
  FieldDateProps,
  FieldDateTime,
  FieldDateTimeProps,
  FieldFile,
  FieldFileProps,
  FieldFloat,
  FieldFloatProps,
  FieldInteger,
  FieldIntegerProps,
  FieldPhone,
  FieldPhoneProps,
  FieldString,
  FieldStringProps,
  FieldText,
  FieldTextProps,
  FieldTime,
  FieldTimeProps,
  FieldYear,
  FieldYearProps
} from './Field';
import {
  useModelFieldBooleanProps,
  useModelFieldCountryProps,
  useModelFieldDateTimeProps,
  useModelFieldEnumProps,
  useModelFieldFileProps,
  useModelFieldInputProps,
  useModelFieldIntegerSelectProps,
  useModelFieldNumberInputProps,
  useModelFieldReferenceProps,
  useModelFieldTimeProps
} from './form';
import { FieldSelect, FieldSelectProps } from './FieldSelect';
import {
  getIdentifierAttribute,
  getModelFromRef
} from '@rhino-project/core/utils';
import { useController } from 'react-hook-form';
import {
  ComboBox,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  FieldError,
  Spinner
} from '@heroui/react';
import { keepPreviousData } from '@tanstack/react-query';
import type {
  RhinoFilter,
  RhinoOrder,
  RhinoResourceName
} from '@rhino-project/core';

// Types
export type ModelFieldProps = {
  model: string | Record<string, unknown>;
  attribute: string;
};
export type ModelFieldPhoneProps = FieldPhoneProps & ModelFieldProps;

/* The referenced model is resolved from the attribute at `path`, which only
 * happens at render, so a caller that wants its filter and order checked names
 * the target: <ModelFieldReference<'player'> path="player" ... />. Unnamed,
 * both stay as loose as they were. */
export type ModelFieldReferenceProps<T = unknown> = {
  path: string;
  label?: React.ReactNode;
  filter?: T extends RhinoResourceName
    ? RhinoFilter<T>
    : Record<string, unknown>;
  limit?: number;
  offset?: number;
  order?: T extends RhinoResourceName ? RhinoOrder<T> : string[];
};
export type ModelFieldOwnerReferenceProps<T = unknown> =
  ModelFieldReferenceProps<T>;

// Boolean
export const ModelFieldBooleanBase: React.FC<FieldBooleanProps> = (props) => {
  const fieldProps = useModelFieldBooleanProps(props);

  return <FieldBoolean {...fieldProps} />;
};

// Country
export const ModelFieldCountryBase: React.FC<FieldCountryProps> = (props) => {
  const fieldProp = useModelFieldCountryProps(props);

  return <FieldCountry {...fieldProp} />;
};

// Currency
export const ModelFieldCurrencyBase: React.FC<FieldCurrencyProps> = (props) => {
  const fieldProps = useModelFieldNumberInputProps(props);

  return <FieldCurrency {...fieldProps} />;
};

// Date
export const ModelFieldDateBase: React.FC<FieldDateProps> = (props) => {
  const fieldProps = useModelFieldDateTimeProps(props);

  return <FieldDate {...fieldProps} />;
};

// Date Time
export const ModelFieldDateTimeBase: React.FC<FieldDateTimeProps> = (props) => {
  const fieldProps = useModelFieldDateTimeProps(props);

  return <FieldDateTime {...fieldProps} />;
};

// Enum
export const ModelFieldEnumBase: React.FC<FieldSelectProps> = (props) => {
  const fieldProps = useModelFieldEnumProps(props);

  return <FieldSelect {...fieldProps} />;
};

// File
export const ModelFieldFileBase: React.FC<FieldFileProps> = (props) => {
  const fieldProps = useModelFieldFileProps(props);

  return <FieldFile {...fieldProps} />;
};

// Float
export const ModelFieldFloatBase: React.FC<FieldFloatProps> = (props) => {
  const fieldProps = useModelFieldNumberInputProps(props);

  return <FieldFloat {...fieldProps} />;
};

// Integer
export const ModelFieldIntegerBase: React.FC<FieldIntegerProps> = (props) => {
  const fieldProps = useModelFieldNumberInputProps(props);

  return <FieldInteger {...fieldProps} />;
};

// Integer Select
export const ModelFieldIntegerSelectBase: React.FC<FieldSelectProps> = (
  props
) => {
  const fieldProps = useModelFieldIntegerSelectProps(props);

  return <FieldSelect {...fieldProps} />;
};

// Owner Reference
export const ModelFieldOwnerReferenceBase = <T,>({
  filter: extraFilters,
  ...props
}: ModelFieldOwnerReferenceProps<T>) => {
  const { path } = props;
  const { model } = useModelContext();
  const { attribute } = useModelAndAttributeFromPath(model!, path);
  const refModel = useMemo(() => getModelFromRef(attribute!), [attribute])!;
  const filter = useBaseOwnerFilters(refModel, { extraFilters });

  return (
    <ModelFieldReference
      // The owner filter is built from the model resolved at render, which the
      // register has no name for at this point.
      filter={filter as ModelFieldReferenceProps<T>['filter']}
      {...props}
    />
  );
};

// Phone
export const ModelFieldPhoneBase: React.FC<ModelFieldPhoneProps> = (props) => {
  const fieldProps = useModelFieldInputProps(props);

  return <FieldPhone {...fieldProps} />;
};

// Reference
// A row of the referenced model, which is chosen at runtime, so the register
// cannot say which resource these results hold. Rhino serializes a reference's
// label as display_name.
type ReferenceRow = { [name: string]: unknown; display_name: string };

export const ModelFieldReferenceBase = <T,>({
  filter: propsFilter,
  limit = 10,
  offset,
  order,
  ...props
}: ModelFieldReferenceProps<T>) => {
  const { t } = useTranslation();
  const { path } = props;
  const { model } = useModelContext();
  const { label, ...fieldProps } = useModelFieldReferenceProps(props);
  const { attribute } = useModelAndAttributeFromPath(model!, path);
  const refModel = useMemo(() => getModelFromRef(attribute!), [attribute])!;
  const identifier = useMemo(
    () => getIdentifierAttribute(refModel),
    [refModel]
  )!;
  const {
    field: { disabled, value, onChange, ...rhfProps },
    fieldState: { error }
  } = useController({
    name: path
  });

  // The value might be a whole object or just the identifier
  const valString = useMemo(
    () =>
      value?.[identifier.name] ? `${value?.[identifier.name]}` : `${value}`,
    [value, identifier]
  );

  const [search, setSearch] = useState('');

  // Filter by identifier if value is set
  const filter = useMemo(() => {
    if (!value) return propsFilter;

    return { ...propsFilter, [identifier.name]: valString };
  }, [identifier.name, propsFilter, valString, value]);

  const { results, isLoading } = useModelIndex(refModel, {
    search,
    filter,
    limit,
    offset,
    order,
    queryOptions: {
      // Keep previous data so that the selected option doesn't disappear when selected
      // as the results are refetched with the new filter based on valString
      placeholderData: keepPreviousData
    }
  });

  return (
    <ComboBox
      {...rhfProps}
      onInputChange={setSearch}
      value={valString}
      onChange={(a) => onChange(a)}
      isInvalid={!!error}
      isDisabled={disabled}
      {...fieldProps}
    >
      {label && <Label>{label}</Label>}
      <ComboBox.InputGroup>
        <Input placeholder={t('rhino.frontend.messages.search')} />
        <ComboBox.Trigger />
      </ComboBox.InputGroup>
      <ComboBox.Popover>
        {isLoading ? (
          <div className="flex justify-center p-4">
            <Spinner size="sm" />
          </div>
        ) : (
          <ListBox items={(results || []) as unknown as ReferenceRow[]}>
            {(item: ReferenceRow) => (
              <ListBoxItem
                id={String(item[identifier.name])}
                key={String(item[identifier.name])}
                textValue={item.display_name}
              >
                {item.display_name}
              </ListBoxItem>
            )}
          </ListBox>
        )}
      </ComboBox.Popover>
      <FieldError>{error?.message}</FieldError>
    </ComboBox>
  );
};

// String
export const ModelFieldStringBase: React.FC<FieldStringProps> = (props) => {
  const fieldProps = useModelFieldInputProps(props);

  return <FieldString {...fieldProps} />;
};

// Text
export const ModelFieldTextBase: React.FC<FieldTextProps> = (props) => {
  const fieldProps = useModelFieldInputProps(props);

  return <FieldText {...fieldProps} />;
};

// Time
export const ModelFieldTimeBase: React.FC<FieldTimeProps> = (props) => {
  const fieldProps = useModelFieldTimeProps(props);

  return <FieldTime {...fieldProps} />;
};

// Year
export const ModelFieldYearBase: React.FC<FieldYearProps> = (props) => {
  const fieldProps = useModelFieldNumberInputProps(props);

  return <FieldYear {...fieldProps} />;
};

// Overrideable component exports
export const ModelFieldBoolean: React.FC<FieldBooleanProps> = (props) =>
  useGlobalComponentForAttribute(
    'ModelFieldBoolean',
    ModelFieldBooleanBase,
    props
  );

export const ModelFieldCountry: React.FC<ModelFieldProps> = (props) =>
  useGlobalComponentForAttribute(
    'ModelFieldCountry',
    ModelFieldCountryBase,
    props
  );

export const ModelFieldCurrency: React.FC<FieldCurrencyProps> = (props) =>
  useGlobalComponentForAttribute(
    'ModelFieldCurrency',
    ModelFieldCurrencyBase,
    props
  );

export const ModelFieldDate: React.FC<FieldDateProps> = (props) =>
  useGlobalComponentForAttribute('ModelFieldDate', ModelFieldDateBase, props);

export const ModelFieldDateTime: React.FC<FieldDateTimeProps> = (props) =>
  useGlobalComponentForAttribute(
    'ModelFieldDateTime',
    ModelFieldDateTimeBase,
    props
  );

export const ModelFieldEnum: React.FC<FieldSelectProps> = (props) =>
  useGlobalComponentForAttribute('ModelFieldEnum', ModelFieldEnumBase, props);

export const ModelFieldFile: React.FC<FieldFileProps> = (props) =>
  useGlobalComponentForAttribute('ModelFieldFile', ModelFieldFileBase, props);

export const ModelFieldFloat: React.FC<FieldFloatProps> = (props) =>
  useGlobalComponentForAttribute('ModelFieldFloat', ModelFieldFloatBase, props);

export const ModelFieldInteger: React.FC<FieldIntegerProps> = (props) =>
  useGlobalComponentForAttribute(
    'ModelFieldInteger',
    ModelFieldIntegerBase,
    props
  );

export const ModelFieldIntegerSelect: React.FC<FieldIntegerProps> = (props) =>
  useGlobalComponentForAttribute(
    'ModelFieldIntegerSelect',
    ModelFieldIntegerSelectBase,
    props
  );

export const ModelFieldPhone: React.FC<ModelFieldPhoneProps> = (props) =>
  useGlobalComponentForAttribute('ModelFieldPhone', ModelFieldPhoneBase, props);

export const ModelFieldOwnerReference = <T,>(
  props: ModelFieldOwnerReferenceProps<T>
) =>
  useGlobalComponentForAttribute(
    'ModelFieldOwnerReference',
    ModelFieldOwnerReferenceBase,
    props
  );

export const ModelFieldReference = <T,>(props: ModelFieldReferenceProps<T>) =>
  useGlobalComponentForAttribute(
    'ModelFieldReference',
    ModelFieldReferenceBase,
    props
  );

export const ModelFieldString: React.FC<FieldStringProps> = (props) =>
  useGlobalComponentForAttribute(
    'ModelFieldString',
    ModelFieldStringBase,
    props
  );

export const ModelFieldText: React.FC<FieldTextProps> = (props) =>
  useGlobalComponentForAttribute('ModelFieldText', ModelFieldTextBase, props);

export const ModelFieldTime: React.FC<FieldTimeProps> = (props) =>
  useGlobalComponentForAttribute('ModelFieldTime', ModelFieldTimeBase, props);

export const ModelFieldYear: React.FC<FieldYearProps> = (props) =>
  useGlobalComponentForAttribute('ModelFieldYear', ModelFieldYearBase, props);
